import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

const links = [
  { label: "Services", href: "/#services" },
  { label: "Process", href: "/#process" },
  { label: "About", href: "/about", route: true },
  { label: "Insights", href: "/insights", route: true },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/75 backdrop-blur-xl border-b border-black/10"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1500px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Logo to="/" size="h-10 md:h-12" className="" />

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) =>
            l.route ? (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="font-mono text-xs uppercase tracking-[0.18em] text-foreground/70 hover:text-primary transition-colors"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="font-mono text-xs uppercase tracking-[0.18em] text-foreground/70 hover:text-primary transition-colors"
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <button
              data-testid="nav-dashboard-btn"
              onClick={() => navigate("/portal")}
              className="hidden sm:flex items-center gap-1.5 bg-foreground text-background px-4 py-2.5 font-mono text-xs uppercase tracking-wider hover:bg-primary transition-colors"
            >
              Dashboard <ArrowUpRight size={14} />
            </button>
          ) : (
            <button
              data-testid="nav-login-btn"
              onClick={() => navigate("/login")}
              className="hidden sm:block font-mono text-xs uppercase tracking-wider text-foreground/70 hover:text-primary transition-colors"
            >
              Client Login
            </button>
          )}
          <a
            href="#quote"
            data-testid="nav-quote-btn"
            className="hidden sm:flex items-center gap-1.5 bg-primary text-white px-4 py-2.5 font-mono text-xs uppercase tracking-wider hover:bg-foreground transition-colors"
          >
            Get a Quote
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white border-b border-black/10"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {links.map((l) =>
                l.route ? (
                  <Link key={l.href} to={l.href} onClick={() => setOpen(false)} className="font-display text-2xl font-bold uppercase tracking-tight">
                    {l.label}
                  </Link>
                ) : (
                  <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="font-display text-2xl font-bold uppercase tracking-tight">
                    {l.label}
                  </a>
                )
              )}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => navigate(user ? "/portal" : "/login")}
                  className="flex-1 border border-foreground py-3 font-mono text-xs uppercase"
                >
                  {user ? "Dashboard" : "Login"}
                </button>
                <a href="#quote" onClick={() => setOpen(false)} className="flex-1 bg-primary text-white py-3 text-center font-mono text-xs uppercase">
                  Get a Quote
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
